/**
 * Course Creation/Edit Modal Component
 * Coláiste Lurgan Management System - Course CRUD modal
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../hooks/use-toast';

// UI Components
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

// Icons
import {
  BookOpen,
  Calendar,
  Users,
  Euro,
  Loader2,
  CheckCircle,
} from 'lucide-react';

// Hooks
import { useCreateCourse, useUpdateCourse } from '../../hooks/useCourses';

// Validation Schema
const courseSchema = z.object({
  name: z.string()
    .min(3, 'Course name must be at least 3 characters')
    .max(100, 'Course name must not exceed 100 characters'),
  
  abbreviation: z.string()
    .min(1, 'Abbreviation must be at least 1 character')
    .max(10, 'Abbreviation must not exceed 10 characters')
    .regex(/^[A-Z0-9-]+$/, 'Use only uppercase letters, numbers, and hyphens'),
  
  description: z.string()
    .max(1000, 'Description must not exceed 1000 characters')
    .optional(),
  
  start_date: z.string()
    .min(1, 'Start date is required'),
  
  end_date: z.string()
    .min(1, 'End date is required'),
  
  student_capacity: z.number({
      required_error: 'Student capacity is required',
      invalid_type_error: 'Student capacity must be a number',
    })
    .min(1, 'Capacity must be at least 1'),

  price: z.number({
      required_error: 'Price is required',
      invalid_type_error: 'Price must be a number',
    })
    .min(0, 'Price cannot be negative')
    .max(99999.99, 'Price too large'),

  cinnire_price: z.number({
      required_error: 'Cinnire price is required',
      invalid_type_error: 'Cinnire price must be a number',
    })
    .min(0, 'Cinnire price cannot be negative')
    .max(99999.99, 'Cinnire price too large'),

  num_classes: z.number({
      required_error: 'Number of classes is required',
      invalid_type_error: 'Number of classes must be a number',
    })
    .int('Must be a whole number')
    .min(1, 'Must have at least 1 class')
    .max(26, 'Maximum 26 classes (A–Z)'),
}).refine((data) => {
  if (data.start_date && data.end_date) {
    return new Date(data.end_date) > new Date(data.start_date);
  }
  return true;
}, {
  message: "End date must be after start date",
  path: ["end_date"],
});

export function CourseModal({
  isOpen,
  onClose,
  course = null, // If provided, this is an edit modal
  onSuccess
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!course;
  const classesLocked = isEditing && !!course?.has_class_assignments;

  // Mutations
  const createCourseMutation = useCreateCourse();
  const updateCourseMutation = useUpdateCourse();

  // Form setup
  const form = useForm({
    resolver: zodResolver(courseSchema),
    defaultValues: {
      name: course?.name || '',
      abbreviation: course?.abbreviation || '',
      description: course?.description || '',
      start_date: course?.start_date || '',
      end_date: course?.end_date || '',
      student_capacity: course?.student_capacity || undefined,
      price: course?.price ? parseFloat(course.price) : undefined,
      cinnire_price: course?.cinnire_price ? parseFloat(course.cinnire_price) : undefined,
      num_classes: course?.num_classes ?? 13,
    },
  });

  // Reset form when course prop changes (for editing)
  useEffect(() => {
    if (course) {
      form.reset({
        name: course.name || '',
        abbreviation: course.abbreviation || '',
        description: course.description || '',
        start_date: course.start_date || '',
        end_date: course.end_date || '',
        student_capacity: course.student_capacity || undefined,
        price: course.price ? parseFloat(course.price) : undefined,
        cinnire_price: course.cinnire_price ? parseFloat(course.cinnire_price) : undefined,
        num_classes: course.num_classes ?? 13,
      });
    } else {
      // Reset to empty form for new course
      form.reset({
        name: '',
        abbreviation: '',
        description: '',
        start_date: '',
        end_date: '',
        student_capacity: undefined,
        price: undefined,
        cinnire_price: undefined,
        num_classes: 13,
      });
    }
  }, [course, form]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      // Convert string numbers to actual numbers
      const courseData = {
        ...data,
        student_capacity: Number(data.student_capacity),
        price: Number(data.price),
        cinnire_price: Number(data.cinnire_price),
        num_classes: Number(data.num_classes),
      };

      if (isEditing) {
        await updateCourseMutation.mutateAsync({
          id: course.uuid,
          ...courseData,
        });
      } else {
        await createCourseMutation.mutateAsync(courseData);
      }
      
      // Success
      onSuccess?.(courseData, isEditing);
      onClose();
      
      // Reset form for next use
      if (!isEditing) {
        form.reset();
      }
      
    } catch (error) {
      console.error('Course submission error:', error);

      // Extract error message from API response
      let errorMessage = t('courses.error.saveFailed', 'Failed to save course.');

      if (error.response?.data) {
        // Handle field-specific errors
        const data = error.response.data;
        if (data.name) {
          errorMessage = Array.isArray(data.name) ? data.name[0] : data.name;
        } else if (data.student_capacity) {
          errorMessage = Array.isArray(data.student_capacity) ? data.student_capacity[0] : data.student_capacity;
        } else if (data.price) {
          errorMessage = Array.isArray(data.price) ? data.price[0] : data.price;
        } else if (data.cinnire_price) {
          errorMessage = Array.isArray(data.cinnire_price) ? data.cinnire_price[0] : data.cinnire_price;
        } else if (data.start_date) {
          errorMessage = Array.isArray(data.start_date) ? data.start_date[0] : data.start_date;
        } else if (data.end_date) {
          errorMessage = Array.isArray(data.end_date) ? data.end_date[0] : data.end_date;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (data.non_field_errors) {
          errorMessage = Array.isArray(data.non_field_errors) ? data.non_field_errors[0] : data.non_field_errors;
        }
      }

      toast({
        title: t('common.error', 'Error'),
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      // Reset form when closing
      if (!isEditing) {
        form.reset();
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            {isEditing 
              ? t('courses.edit.title', 'Edit Course')
              : t('courses.create.title', 'Create New Course')
            }
          </DialogTitle>
          <DialogDescription>
            {isEditing 
              ? t('courses.edit.description', 'Update course information and settings')
              : t('courses.create.description', 'Add a new course to the system')
            }
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            
            {/* Basic Information Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                {t('courses.form.basicInfo', 'Basic Information')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Course Name */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('courses.form.name', 'Course Name')}</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder={t('courses.form.namePlaceholder', 'e.g., Beginners Course A')}
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                {/* Course Abbreviation */}
                <FormField
                  control={form.control}
                  name="abbreviation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('courses.form.abbreviation', 'Abbreviation')}</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder={t('courses.form.abbreviationPlaceholder', 'e.g., BEG-A')}
                          {...field} 
                        />
                      </FormControl>
                      <FormDescription>
                        {t('courses.form.abbreviationHint', 'Short code for the course (uppercase letters, numbers, hyphens)')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              {/* Course Description */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('courses.form.description', 'Description (Optional)')}</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder={t('courses.form.descriptionPlaceholder', 'Course description and requirements...')}
                        rows={3}
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Dates Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {t('courses.form.dates', 'Course Dates')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Start Date */}
                <FormField
                  control={form.control}
                  name="start_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('courses.form.startDate', 'Start Date')}</FormLabel>
                      <FormControl>
                        <Input 
                          type="date"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                {/* End Date */}
                <FormField
                  control={form.control}
                  name="end_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('courses.form.endDate', 'End Date')}</FormLabel>
                      <FormControl>
                        <Input 
                          type="date"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Capacity and Pricing Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Users className="h-4 w-4" />
                {t('courses.form.capacityAndPricing', 'Capacity & Pricing')}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Student Capacity */}
                <FormField
                  control={form.control}
                  name="student_capacity"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('courses.form.capacity', 'Student Capacity')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          placeholder="50"
                          onFocus={(e) => e.target.value && e.target.select()}
                          {...field}
                          onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                        />
                      </FormControl>
                      <FormDescription>
                        {t('courses.form.capacityHint', 'Maximum number of students')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Number of Classes */}
                <FormField
                  control={form.control}
                  name="num_classes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('courses.form.numClasses', 'Number of Classes')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          max="26"
                          disabled={classesLocked}
                          {...field}
                          onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                        />
                      </FormControl>
                      <FormDescription>
                        {classesLocked
                          ? t('courses.form.numClassesLocked', 'Cannot change while students are assigned to classes')
                          : t('courses.form.numClassesHint', 'Classes are named Rang A, Rang B… up to Rang Z (max 26)')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Regular Price */}
                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1">
                        <Euro className="h-3 w-3" />
                        {t('courses.form.price', 'Regular Price (EUR)')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          onFocus={(e) => e.target.value && e.target.select()}
                          {...field}
                          onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                        />
                      </FormControl>
                      <FormDescription>
                        {t('courses.form.priceHint', 'Course fee in euros')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Cinnire Price */}
                <FormField
                  control={form.control}
                  name="cinnire_price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1">
                        <Euro className="h-3 w-3" />
                        {t('courses.form.cinnirePrice', 'Cinnire Price (EUR)')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          onFocus={(e) => e.target.value && e.target.select()}
                          {...field}
                          onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                        />
                      </FormControl>
                      <FormDescription>
                        {t('courses.form.cinnirepPriceHint', 'Course fee for cinnire (leaders)')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end gap-3 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="gap-2"
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4" />
                )}
                {isSubmitting 
                  ? t('common.processing', 'Processing...')
                  : isEditing 
                    ? t('courses.form.update', 'Update Course')
                    : t('courses.form.create', 'Create Course')
                }
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default CourseModal;